import ApplePayPaymentAuthorizedEvent = ApplePayJS.ApplePayPaymentAuthorizedEvent;
import ApplePayPaymentRequest = ApplePayJS.ApplePayPaymentRequest;
import store from '../../../store/store';
import { APPLE_PAY_STORE_NAME, getLastCardDigits, isApplePaySupported } from './utils/apple-pay.util';
import { ApplePayButtonAnalytics } from './apple-pay-button.analytics';
import { ApplePayPaymentPayload } from './interfaces/payment-method-data.interface';
import { ApplePaySessionUtil } from './utils/apple-pay-session.util';
import { BaseComponent } from '../../base-component/base-component';
import { PaymentDetails, PaymentType } from '../../checkout/shop-checkout.interface';
import { StoreState } from '../../../store/store.interface';
import { booleanConverter } from '../../../common/converters';
import { connect } from 'pwa-helpers';
import { createApplePayInstance } from './thunks/apple-pay-instance.thunk';
import { createDataCollector, getDataCollector } from '../braintree';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { getApplePayInstance, isApplePayReadyToUse } from './store/apple-pay.selectors';
import { loading } from '../../../common/constants/api-constants';
import { mapAppleAddress } from './mappings/apple-pay-address.mapping';
import { waitForElementsToLoad } from '../../../common/utils/delay-util';

const SKELETON_WRAPPER_CSS_CLASS_SMALL = 'small-size-skeleton';
const SKELETON_WRAPPER_CSS_CLASS_LARGE = 'large-size-skeleton';

/**
 * Configures and loads the Apple Pay button.
 * Saves Apple Pay payment details in redux.
 */
@customElement('mhecomm-apple-pay-button')
export default class ApplePayButton extends connect(store)(BaseComponent) {
  static styles = [
    css`
      .small-size-skeleton.loading {
        width: 240px;
        height: 40px;
      }

      #apple-pay-button {
        --apple-pay-button-height: 40px;
        --apple-pay-button-box-sizing: border-box;
      }

      .small-size-skeleton #apple-pay-button {
        --apple-pay-button-width: 240px;
        --apple-pay-button-padding: 15px 0px;
        --apple-pay-button-border-radius: 5px;
      }

      .large-size-skeleton #apple-pay-button {
        --apple-pay-button-padding: 3px 0px;
        margin-bottom: 0.5rem;
        width: 100%;
      }
    `,
  ];

  /**
   * Transaction amount to be used on Apple Pay.
   */
  @property({type: String})
  amount: string = '0.00';

  /**
   * Button color for the Apple Pay button.
   */
  @property()
  buttonColor: 'black' | 'white' | 'white-outline' = 'black';

  /**
   * Button size for the Apple Pay button.
   */
  @property()
  buttonSize: 'small' | 'large' = 'small';

  /**
   * Button type for the Apple Pay button.
   */
  @property()
  buttonType: 'buy' | 'check-out' = 'buy';

  /**
   * Braintree Client token to initialize the Apple Pay button.
   */
  @property()
  clientToken: string = '';

  /**
   * Country for Apple Pay transaction.
   */
  @property()
  countryCode: string = 'US';

  /**
   * Currency for Apple Pay transaction.
   */
  @property()
  currencyCode: string = 'USD';

  /**
   * Locale for the Apple Pay button.
   */
  @property()
  locale: string = 'en_US';

  /**
   * Margins for the Apple Pay button loader.
   */
  @property()
  loaderMargins = 'mt-0';

  /**
   * Flag to indicate the checkout contains shippable items.
   */
  @property({converter: booleanConverter})
  shippable: boolean = false;

  /**
   * Flag to indicate the Apple Pay button is loading.
   */
  @state()
  isLoading: boolean = true;

  /**
   * Apple Pay payment details.
   */
  @state()
  private paymentDetails: PaymentDetails = {};

  /**
   * Analytics instance for the Apple Pay events.
   */
  private analytics: ApplePayButtonAnalytics;

  /**
   * Session utils instance for Apple Pay.
   */
  private applePaySessionUtil: ApplePaySessionUtil;

  constructor() {
    super();
    this.analytics = new ApplePayButtonAnalytics();
    this.applePaySessionUtil = new ApplePaySessionUtil();
  }

  /**
   * Sets up the Apple Pay button when the component is ready.
   *
   * @param _changedProperties
   */
  async firstUpdated(_changedProperties: Map<string | number | symbol, unknown>) {
    super.firstUpdated(_changedProperties);
    await this.setUpApplePayButton();
  }

  renderComp() {
    return html`
      <div
        id="apple-pay-wrapper-div"
        class="loading ${this.buttonSize === 'small' ? SKELETON_WRAPPER_CSS_CLASS_SMALL : SKELETON_WRAPPER_CSS_CLASS_LARGE}">
        ${this.isLoading
          ? html`
            ${loading({
              columns: 1,
              width: '100%',
              height: '2.5rem',
              m: this.loaderMargins,
            })}`
          : nothing
        }
        <div id="apple-pay-button-container" class="d-none"></div>
      </div>
    `;
  }

  /**
   * Gets the button container element.
   *
   * @private
   */
  private getButtonContainer(): HTMLElement {
    return this.shadowRoot!.querySelector('#apple-pay-button-container') as HTMLElement;
  }

  /**
   * Gets the wrapper div element.
   *
   * @private
   */
  private getWrapperDiv(): HTMLElement {
    return this.shadowRoot!.querySelector('#apple-pay-wrapper-div') as HTMLElement;
  }

  /**
   * Apple Pay button initialization (instance and platform checks).
   */
  async setUpApplePayButton(): Promise<void> {
    try {
      if (!isApplePaySupported()) {
        console.warn(`Current device doesn't support apple pay.`);
        this.isLoading = false;
        return;
      }

      await store.dispatch(createApplePayInstance({clientToken: this.clientToken}));
      if (isApplePayReadyToUse(store.getState() as StoreState)) {
        await this.createApplePayButton();
      }
    } catch (err) {
      console.error(`Failed to configure Apple Pay: ${(err as Error).message}`);
      this.isLoading = false;
    }
  }

  /**
   * Creates the Apple Pay button element.
   *
   * The button should be mounted, as it is done for the Google Pay button.
   * This prevents display issues (button not displayed, temporary incorrect label until Apple Pay is parsing the DOM).
   */
  createButtonDomElement(): HTMLElement {
    if (!customElements.get('apple-pay-button')) {
      customElements.define('apple-pay-button', HTMLElement);
    }

    const applePayButton: HTMLElement = document.createElement('apple-pay-button');
    applePayButton.setAttribute('id', 'apple-pay-button');
    applePayButton.setAttribute('type', this.buttonType);
    applePayButton.setAttribute('buttonstyle', this.buttonColor);
    applePayButton.setAttribute('locale', this.locale);
    applePayButton.onclick = this.handlePayment.bind(this);

    return applePayButton;
  }

  /**
   * Displays the Apple Pay button when everything is ready.
   */
  async createApplePayButton(): Promise<void> {
    waitForElementsToLoad(this.shadowRoot!, ['#apple-pay-wrapper-div']).then(() => {
      this.getButtonContainer().appendChild(this.createButtonDomElement());

      waitForElementsToLoad(this.shadowRoot!, ['#apple-pay-button']).then(() => {
        this.isLoading = false;
        this.getWrapperDiv().classList.remove('loading');
        this.getButtonContainer().classList.remove('d-none');
        this.readyToAcceptPayment();
      });
    });
  }

  /**
   * Dispatches an event to notify the Apple Pay button is ready to accept payment.
   */
  private readyToAcceptPayment(): void {
    this.dispatchEvent(new CustomEvent(
      'mhecomm-apple-pay-button-ready-to-payment',
      {bubbles: true, composed: true}
    ));
  }

  /**
   * Handles the Apple Pay payment event.
   *
   * @param event
   */
  async handlePayment(event: Event): Promise<void> {
    event.preventDefault();
    const state = store.getState() as StoreState;
    this.analytics.startEvent(state, this, this.amount);

    this.applePaySessionUtil.createSession(this.getPaymentDataRequest(state), this);
  }

  /**
   * Handles the Apple Pay payment response.
   * It triggers an event with the payment details.
   *
   * @param event
   * @param payload
   */
  handlePaymentResponse(event: ApplePayPaymentAuthorizedEvent, payload: ApplePayPaymentPayload): void {
    this.buildPaymentDetails(payload)
      .then((paymentDetails): void => {
        this.paymentDetails = paymentDetails;

        this.analytics.checkoutInfoCollected(
          this,
          this.paymentDetails,
          mapAppleAddress(event.payment.billingContact, event.payment.shippingContact?.phoneNumber),
          this.shippable ? mapAppleAddress(event.payment.shippingContact) : undefined
        );
      });
  }

  /**
   * Creates the Apple Pay payment request.
   * The phone number on the billing contact does not appear on the Apple Pay sheet.
   * Therefore, we require it on the shipping contact.
   * If the quote is shippable, we require the postal address too.
   *
   * @param state
   */
  getPaymentDataRequest(state: StoreState): ApplePayPaymentRequest {
    return getApplePayInstance(state).createPaymentRequest({
      countryCode: this.countryCode,
      currencyCode: this.currencyCode,
      requiredBillingContactFields: ['phone', 'postalAddress'],
      requiredShippingContactFields: this.shippable ? ['phone', 'postalAddress'] : ['phone'],
      total: {
        label: APPLE_PAY_STORE_NAME,
        amount: this.amount
      }
    });
  }

  /**
   * Builds the payment details to be saved in the component.
   *
   * @param result
   */
  async buildPaymentDetails(result: ApplePayPaymentPayload) {
    await store.dispatch(
      createDataCollector({
        clientToken: this.clientToken,
      })
    );
    return {
      paymentType: PaymentType.BRAINTREE_APPLE_PAY,
      paymentNonce: result.nonce,
      thirdPartyDetails: {
        cardType: result.details.cardType,
        lastDigits: getLastCardDigits(result.details.paymentInstrumentName),
      },
      deviceData: getDataCollector(store.getState() as StoreState).deviceData,
    };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-apple-pay-button': ApplePayButton;
  }
}
